import { StatCard } from '@/components/ui/stat-card';
import { Zap, Bot, Puzzle, Star, TrendingUp, Users, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { trpc } from '@/lib/trpc';
import { formatRelativeDate } from '@/lib/skills-store';
import { cn } from '@/lib/utils';

const TYPE_ICON: Record<string, React.ReactNode> = {
  skill: <Zap className="h-3.5 w-3.5" />,
  agent: <Bot className="h-3.5 w-3.5" />,
  plugin: <Puzzle className="h-3.5 w-3.5" />,
};

const TYPE_COLOR: Record<string, string> = {
  skill: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  agent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  plugin: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
};


function RankList({
  title,
  icon,
  items,
  valueLabel,
  emptyText,
}: {
  title: string;
  icon: React.ReactNode;
  items: { id: string; name: string; type: string; team: string; value: number }[];
  valueKey: string;
  valueLabel: string;
  emptyText: string;
}) {
  const max = items[0]?.value || 1;

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{emptyText}</p>
      ) : (
        <ol className="space-y-2.5">
          {items.map((item, i) => (
            <li key={item.id} className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-xs font-semibold text-muted-foreground text-right">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium', TYPE_COLOR[item.type])}>
                    {TYPE_ICON[item.type]}
                    {item.type}
                  </span>
                  <span className="text-sm font-medium truncate">{item.name}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${Math.max(4, (item.value / max) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums">
                {item.value} <span className="text-xs text-muted-foreground font-normal">{valueLabel}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export function SkillsAnalyticsTab() {
  const { t } = useTranslation();
  const { data: metrics, isLoading } = trpc.skills.getMetrics.useQuery();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!metrics) return null;

  const totalAll = metrics.totalsByType.skill + metrics.totalsByType.agent + metrics.totalsByType.plugin;
  const maxTeam = metrics.uploadsByTeam[0]?.total || 1;

  const topByUse = metrics.topByUseCount.map((s) => ({ ...s, value: s.useCount }));
  const topByFav = metrics.topByFavorites.map((s) => ({ ...s, value: s.favoriteCount }));

  return (
    <div className="space-y-6 mt-4">
      {/* Totals */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard layout="left" tone="primary" icon={<BarChart3 className="h-5 w-5" />} label={t('skills.analytics.total_all')} value={totalAll} />
        <StatCard layout="left" tone="primary" icon={<Zap className="h-5 w-5" />} label={t('skills.analytics.total_skills')} value={metrics.totalsByType.skill} />
        <StatCard layout="left" tone="primary" icon={<Bot className="h-5 w-5" />} label={t('skills.analytics.total_agents')} value={metrics.totalsByType.agent} />
        <StatCard layout="left" tone="primary" icon={<Puzzle className="h-5 w-5" />} label={t('skills.analytics.total_plugins')} value={metrics.totalsByType.plugin} />
      </div>

      {/* Source breakdown */}
      <div className="flex gap-3">
        <Badge variant="outline" className="gap-1.5 px-3 py-1.5 text-sm">
          <span className="h-2 w-2 rounded-full bg-primary inline-block" />
          {metrics.totalsBySource.official} {t('skills.analytics.official')}
        </Badge>
        <Badge variant="outline" className="gap-1.5 px-3 py-1.5 text-sm">
          <span className="h-2 w-2 rounded-full bg-muted-foreground inline-block" />
          {metrics.totalsBySource.community} {t('skills.analytics.community')}
        </Badge>
      </div>

      {/* Top lists */}
      <div className="grid gap-4 lg:grid-cols-2">
        <RankList
          title={t('skills.analytics.top_used')}
          icon={<TrendingUp className="h-4 w-4" />}
          items={topByUse}
          valueKey="useCount"
          valueLabel={t('skills.analytics.uses')}
          emptyText={t('skills.analytics.no_use_data')}
        />
        <RankList
          title={t('skills.analytics.top_favorites')}
          icon={<Star className="h-4 w-4" />}
          items={topByFav}
          valueKey="favoriteCount"
          valueLabel={t('skills.analytics.favs')}
          emptyText={t('skills.analytics.no_fav_data')}
        />
      </div>

      {/* Uploads by team */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">{t('skills.analytics.by_team')}</h3>
        </div>
        {metrics.uploadsByTeam.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('skills.analytics.no_data')}</p>
        ) : (
          <div className="space-y-3">
            {metrics.uploadsByTeam.map((row) => (
              <div key={row.team} className="flex items-center gap-3">
                <span className="w-36 shrink-0 text-sm font-medium truncate">{row.team}</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${Math.max(4, (row.total / maxTeam) * 100)}%` }}
                  />
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums w-6 text-right">{row.total}</span>
                <div className="shrink-0 flex gap-1">
                  {row.skill > 0 && (
                    <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs', TYPE_COLOR.skill)}>
                      {TYPE_ICON.skill}{row.skill}
                    </span>
                  )}
                  {row.agent > 0 && (
                    <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs', TYPE_COLOR.agent)}>
                      {TYPE_ICON.agent}{row.agent}
                    </span>
                  )}
                  {row.plugin > 0 && (
                    <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs', TYPE_COLOR.plugin)}>
                      {TYPE_ICON.plugin}{row.plugin}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent activity */}
      <Card className="p-5 space-y-3">
        <h3 className="font-semibold text-sm">{t('skills.analytics.recent_activity')}</h3>
        <div className="space-y-2">
          {metrics.recentActivity.map((item) => (
            <div key={item.id} className="flex items-center gap-3 text-sm">
              <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs shrink-0', TYPE_COLOR[item.type])}>
                {TYPE_ICON[item.type]}{item.type}
              </span>
              <span className="flex-1 truncate font-medium">{item.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{item.team}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{formatRelativeDate(item.updatedAt)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
